import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import RomStatisticsCircleChart from './RomStatisticsCircleChart';
import RomStatisticsBarChart from './RomStatisticsBarChart';
import RomStatisticsTwoBarChart from './RomStatisticsTwoBarChart';
import NoDataText from 'components/Text/NoDataText';
import Layout from 'components/Layout/Layout';
import useRomXlsx from 'hooks/useRomXlsx';
import useRomXlsxData from 'hooks/useRomXlsxData';
import { titleColor } from 'asserts/styles/colors';
import { useAtomValue } from 'jotai';
import centerInforIdxAtom from 'atom/centerInforIdxAtom';
import companyIdxAtom from 'atom/companyIdxAtom';
import productAtom from 'atom/productAtom';
import useRomSchoolsXlsx from 'hooks/useRomSchoolsXlsx';
import useRomSchoolsXlsxData from 'hooks/useRomSchoolsXlsxData';
import { useMutation, useQuery } from '@tanstack/react-query';
import { QueryKeys, restfetcher } from 'queryClient';
import commonYearAtom from 'atom/commonYear';
import commonMonthAtom from 'atom/commonMonth';

const RomStatisticsLayout = () => {
  const targetRef = useRef<HTMLDivElement | null>(null);

  const centerIdx = useAtomValue(centerInforIdxAtom);
  const selectedYear = useAtomValue(commonYearAtom);
  const selectedMonth = useAtomValue(commonMonthAtom);
  const companyIdx = useAtomValue(companyIdxAtom);
  const productNumber = useAtomValue(productAtom);

  const {
    lightBlue,
    lightEmerald,
    lightLime,
    lightPurlple,
    lightOrange,
    lightLed,
    lightBlueBackGround,
    lightEmeraldBackGround,
    lightLimeBackGround,
    lightPurlpleBackGround,
    lightOrangeBackGround,
    lightLedBackGround
  } = titleColor;

  const param = {
    companyidx: companyIdx,
    centeridx: centerIdx,
    year: selectedYear,
    month: selectedMonth
  };

  const excelParam = {
    companyidx: companyIdx,
    centeridx: centerIdx,
    year: selectedYear,
    month: selectedMonth,
    field: 'excel'
  };

  const queryKey = [QueryKeys.ROMCHART, param];

  const getChartData = useQuery({
    queryKey: queryKey,
    queryFn: () =>
      restfetcher({
        method: 'POST',
        path: 'dashboard/rom/stats',
        params: param
      }),
    enabled: false
  });

  const getChartDetailData = useMutation({
    mutationFn: () =>
      restfetcher({
        method: 'POST',
        path: 'dashboard/rom/stats/detail',
        params: excelParam
      }),

    onSuccess: (data: any) => {
      if (data.result === 'success') {
        if (productNumber === 9 || productNumber === 8) {
          const xlsxData = useRomSchoolsXlsxData(data?.excel, productNumber);
          useRomSchoolsXlsx(xlsxData, productNumber);
        } else {
          const xlsxData = useRomXlsxData(data?.excel, productNumber);
          useRomXlsx(xlsxData, productNumber);
        }
      }
    },
    onError: (error: any) => {
      console.log(error);
    }
  });

  const { data: chartData, refetch: chartDataRefetch } = getChartData;

  const handleClickExcelDownload = async () => {
    getChartDetailData.mutate();
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries, observerInstance) => {
        if (entries[0].isIntersecting) {
          chartDataRefetch();
          observerInstance.disconnect();
        }
      },
      {
        root: null,
        threshold: 0.1
      }
    );

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, [chartDataRefetch, selectedYear, selectedMonth]);

  return (
    <div className="rom-layout">
      <Layout
        title="ROM 분석 통계"
        onExcelDownload={handleClickExcelDownload}
        result={chartData?.result}
      >
        <Wrapper ref={targetRef}>
          {chartData?.result === 'success' ? (
            <>
              <RomStatisticsCircleChart chartData={chartData.rom} />
              <RomStatisticsTwoBarChart
                title="목 측굴"
                isWideWrapper
                leftDetailType="neckLatFlexL"
                rightDetailType="neckLatFlexR"
                leftDataProps={chartData?.rom?.neckLatFlexL}
                rightDataProps={chartData?.rom?.neckLatFlexR}
                colors={lightBlue}
                backgroundColor={lightBlueBackGround}
              />
              <RomStatisticsTwoBarChart
                title="몸통 측굴"
                isWideWrapper
                leftDetailType="trunkLatFlexL"
                rightDetailType="trunkLatFlexR"
                leftDataProps={chartData?.rom?.trunkLatFlexL}
                rightDataProps={chartData?.rom?.trunkLatFlexR}
                colors={lightEmerald}
                backgroundColor={lightEmeraldBackGround}
              />
              <RomStatisticsBarChart chartData={chartData.rom} />
              <RomStatisticsTwoBarChart
                title="어깨 외전"
                isWideWrapper={false}
                leftDetailType="shoulderAbductionL"
                rightDetailType="shoulderAbductionR"
                leftDataProps={chartData?.rom?.shoulderAbductionL}
                rightDataProps={chartData?.rom?.shoulderAbductionR}
                colors={lightLime}
                backgroundColor={lightLimeBackGround}
              />
              <RomStatisticsTwoBarChart
                title="어깨 굴곡"
                isWideWrapper={false}
                leftDetailType="shoulderFlexionL"
                rightDetailType="shoulderFlexionR"
                leftDataProps={chartData?.rom?.shoulderFlexionL}
                rightDataProps={chartData?.rom?.shoulderFlexionR}
                colors={lightLime}
                backgroundColor={lightLimeBackGround}
              />
              <RomStatisticsTwoBarChart
                title="어깨 신전"
                isWideWrapper={false}
                leftDetailType="shoulderExtensionL"
                rightDetailType="shoulderExtensionR"
                leftDataProps={chartData?.rom?.shoulderExtensionL}
                rightDataProps={chartData?.rom?.shoulderExtensionR}
                colors={lightLime}
                backgroundColor={lightLimeBackGround}
              />

              <RomStatisticsTwoBarChart
                title="어깨 내회전"
                isWideWrapper
                leftDetailType="shoulderIntRotationL"
                rightDetailType="shoulderIntRotationR"
                leftDataProps={chartData?.rom?.shoulderIntRotationL}
                rightDataProps={chartData?.rom?.shoulderIntRotationR}
                colors={lightLime}
                backgroundColor={lightLimeBackGround}
              />

              <RomStatisticsTwoBarChart
                title="어깨 외회전"
                isWideWrapper
                leftDetailType="shoulderExtRotationL"
                rightDetailType="shoulderExtRotationR"
                leftDataProps={chartData?.rom?.shoulderExtRotationL}
                rightDataProps={chartData?.rom?.shoulderExtRotationR}
                colors={lightLime}
                backgroundColor={lightLimeBackGround}
              />

              <RomStatisticsTwoBarChart
                title="팔꿈치 굴곡"
                isWideWrapper
                leftDetailType="elbowFlexionL"
                rightDetailType="elbowFlexionR"
                leftDataProps={chartData?.rom?.elbowFlexionL}
                rightDataProps={chartData?.rom?.elbowFlexionR}
                colors={lightPurlple}
                backgroundColor={lightPurlpleBackGround}
              />

              <RomStatisticsTwoBarChart
                title="팔꿈치 신전"
                isWideWrapper
                leftDetailType="elbowExtensionL"
                rightDetailType="elbowExtensionR"
                leftDataProps={chartData?.rom?.elbowExtensionL}
                rightDataProps={chartData?.rom?.elbowExtensionR}
                colors={lightPurlple}
                backgroundColor={lightPurlpleBackGround}
              />

              <RomStatisticsTwoBarChart
                title="무릎 굴곡OKC"
                isWideWrapper
                leftDetailType="kneeFlexionL"
                rightDetailType="kneeFlexionR"
                leftDataProps={chartData?.rom?.kneeFlexionL}
                rightDataProps={chartData?.rom?.kneeFlexionR}
                colors={lightOrange}
                backgroundColor={lightOrangeBackGround}
              />

              <RomStatisticsTwoBarChart
                title="무릎 신전OKC"
                isWideWrapper
                leftDetailType="kneeExtensionL"
                rightDetailType="kneeExtensionR"
                leftDataProps={chartData?.rom?.kneeExtensionL}
                rightDataProps={chartData?.rom?.kneeExtensionR}
                colors={lightOrange}
                backgroundColor={lightOrangeBackGround}
              />

              <RomStatisticsTwoBarChart
                title="고관절 외전"
                isWideWrapper={false}
                leftDetailType="hipAbductionL"
                rightDetailType="hipAbductionR"
                leftDataProps={chartData?.rom?.hipAbductionL}
                rightDataProps={chartData?.rom?.hipAbductionR}
                colors={lightLed}
                backgroundColor={lightLedBackGround}
              />
              <RomStatisticsTwoBarChart
                title="고관절 굴곡"
                isWideWrapper={false}
                leftDetailType="hipFlexionL"
                rightDetailType="hipFlexionR"
                leftDataProps={chartData?.rom?.hipFlexionL}
                rightDataProps={chartData?.rom?.hipFlexionR}
                colors={lightLed}
                backgroundColor={lightLedBackGround}
              />
              <RomStatisticsTwoBarChart
                title="고관절 신전"
                isWideWrapper={false}
                leftDetailType="neckLatFlexL"
                rightDetailType="hipExtensionR"
                leftDataProps={chartData?.rom?.hipExtensionL}
                rightDataProps={chartData?.rom?.hipExtensionR}
                colors={lightLed}
                backgroundColor={lightLedBackGround}
              />

              <RomStatisticsTwoBarChart
                title="고관절 내회전"
                isWideWrapper
                leftDetailType="hipIntRotationL"
                rightDetailType="hipIntRotationR"
                leftDataProps={chartData?.rom?.hipIntRotationL}
                rightDataProps={chartData?.rom?.hipIntRotationR}
                colors={lightLed}
                backgroundColor={lightLedBackGround}
              />

              <RomStatisticsTwoBarChart
                title="고관절 외회전"
                isWideWrapper
                leftDetailType="hipExtRotationL"
                rightDetailType="hipExtRotationR"
                leftDataProps={chartData?.rom?.hipExtRotationL}
                rightDataProps={chartData?.rom?.hipExtRotationR}
                colors={lightLed}
                backgroundColor={lightLedBackGround}
              />
            </>
          ) : (
            <NoDataText />
          )}
        </Wrapper>
      </Layout>
    </div>
  );
};

export default RomStatisticsLayout;

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-flow: wrap;
`;
